import Vue from 'vue'
import Vuex from 'vuex'
import { Company, IUserCompany, UserRoleEnum, IOnboardingPayload, PriceList, Order } from '@/firestore'
import Router from 'vue-router'
import { getHome } from './services/navigation.service'

Vue.use(Vuex)

export interface IState {
  user: any
  userClaim: any
  idToken: string | null
  userCompany: any
  companies: { [companyId: string]: Company } | null
  onboarding: Partial<IOnboardingPayload> | null
  messages: {
    getUserMedia: boolean
  }
  cache: {
    orderNew: Order | null,
    orderEdit: Order | null,
    priceListEdit: PriceList | null
  }
  error: Error | null
  rememberMe: {
    email: string | null,
    phoneNumber: string | null
  }
}

const state: IState = {
  user: null,
  userClaim: null,
  idToken: null,
  userCompany: null,
  companies: null,
  onboarding: null,
  messages: {
    getUserMedia: false
  },
  cache: {
    orderNew: null,
    orderEdit: null,
    priceListEdit: null
  },
  error: null,
  rememberMe: {
    email: null,
    phoneNumber: null
  }
}

export enum Mutations {
  UPDATE_USER = 'updateUser',
  UPDATE_USER_CLAIM = 'updateUserClaim',
  UPDATE_ID_TOKEN = 'updateIdToken',
  UPDATE_COMPANIES = 'updateCompanies',
  UPDATE_USER_COMPANY = 'updateUserCompany',
  UPDATE_ONBOARDING = 'updateOnboarding',
  UPDATE_MESSAGES = 'updateMessages',
  UPDATE_CACHE_ORDER_NEW = 'updateCacheOrderNew',
  UPDATE_CACHE_ORDER_EDIT = 'updateCacheOrderEdit',
  UPDATE_CACHE_PRICELIST_EDIT = 'updateCachePriceListEdit',
  UPDATE_ERROR = 'updateError',
  UPDATE_REMEMBER_ME = 'updateRememberMe',
  INITIALIZE_STORE = 'initializeStore'
}

export enum Actions {
  SET_USER_COMPANY = 'setUserCompany',
  SET_LOGIN = 'setLogin',
  ADD_USER_TO_COMPANY = 'addUserToCompany',
  SET_ERROR = 'setError'
}

const mutations = {
  // @ts-ignore
  [Mutations.UPDATE_USER](lState, {user}) {
    Vue.set(lState, 'user', user)
  },
  // @ts-ignore
  [Mutations.UPDATE_USER_CLAIM](lState, {userClaim}) {
    Vue.set(lState, 'userClaim', userClaim)
  },
  // @ts-ignore
  [Mutations.UPDATE_ID_TOKEN](lState, {idToken}) {
    Vue.set(lState, 'idToken', idToken)
  },
  // @ts-ignore
  [Mutations.UPDATE_COMPANIES](lState, {companies}) {
    Vue.set(lState, 'companies', companies)
  },
  // @ts-ignore
  [Mutations.UPDATE_USER_COMPANY](lstate, {userCompany}) {
    Vue.set(lstate, 'userCompany', userCompany)
  },
  // @ts-ignore
  [Mutations.UPDATE_ONBOARDING](lstate, {onboarding}) {
    Vue.set(lstate, 'onboarding', onboarding)
  },
  [Mutations.UPDATE_MESSAGES](
    lstate: IState,
    {messages}: { messages: Partial<IState['messages']> }
  ) {
    Vue.set(lstate, 'messages', {...lstate.messages, ...messages})
  },
  [Mutations.UPDATE_CACHE_ORDER_NEW](
    lstate: IState,
    {order}: { order: Order | null }
  ) {
    Vue.set(lstate, 'cache', {...lstate.cache, orderNew: order})
  },
  [Mutations.UPDATE_CACHE_ORDER_EDIT](
    lstate: IState,
    {order}: { order: Order | null }
  ) {
    Vue.set(lstate, 'cache', {...lstate.cache, orderEdit: order})
  },
  [Mutations.UPDATE_CACHE_PRICELIST_EDIT](
    lstate: IState,
    {priceList}: { priceList: PriceList | null }
  ) {
    Vue.set(lstate, 'cache', {...lstate.cache, priceListEdit: priceList})
  },
  // @ts-ignore
  [Mutations.UPDATE_ERROR](lstate, {error}) {
    Vue.set(lstate, 'error', error)
  },
  // @ts-ignore
  [Mutations.UPDATE_REMEMBER_ME](lstate, {rememberMe}) {
    Vue.set(lstate, 'rememberMe', rememberMe)
  },
  // @ts-ignore
  [Mutations.INITIALIZE_STORE](lState) {
    const storeString = localStorage.getItem('store')
    if (storeString) {
      const parsedStore = JSON.parse(storeString)
      // @ts-ignore
      this.replaceState({...lState, ...parsedStore})
    }
  }
}

const actions = {
  // @ts-ignore
  // tslint:disable-next-line:no-shadowed-variable
  [Actions.SET_USER_COMPANY]({commit, state}, companyParam) {
    const previousUserCompany = state.userCompany
    let userCompany: IUserCompany | null = null
    let adminsCompany: Company | null = null
    let companyId: string | null = null

    if (companyParam && companyParam instanceof Company) {
      adminsCompany = companyParam
    } else if (companyParam && typeof companyParam === 'string') {
      companyId = companyParam
    }

    if (
      adminsCompany
      && adminsCompany.id
      && state.userClaim
      && state.userClaim.admin
    ) {
      userCompany = {
        ...adminsCompany,
        userRole: UserRoleEnum.ADMIN
      }
    } else if (state.userClaim && state.companies) {
      const userCompanyId = companyId
        || (state.userCompany && state.userCompany.id)
        || state.userClaim.company_id

      let company = null
      if (adminsCompany) {
        company = adminsCompany
      } else {
        company = userCompanyId ? state.companies[userCompanyId] : null
      }

      if (company && company.userRoles) {
        userCompany = {
          ...company,
          userRole: company.userRoles[state.userClaim.user_id]
        }
      }
    }

    if (userCompany && (!state.companies || !state.companies[userCompany.id])) {
      commit(Mutations.UPDATE_COMPANIES, {companies: {...state.companies, [userCompany.id]: userCompany}})
    }

    commit(Mutations.UPDATE_USER_COMPANY, {userCompany})

    return [userCompany, previousUserCompany]
  },

  [Actions.SET_LOGIN]() {
    return true
  },

  [Actions.ADD_USER_TO_COMPANY](
    // @ts-ignore
    // tslint:disable-next-line:no-shadowed-variable
    {commit, state},
    [userId, userRole]: [string, UserRoleEnum]
  ) {
    const users = state.userCompany.users.includes(userId)
      ? state.userCompany.users
      : [...state.userCompany.users, userId]

    const updatedUserCompany = {
      ...state.userCompany,
      userRoles: {
        ...state.userCompany.userRoles,
        [userId]: userRole
      },
      users
    }

    commit(Mutations.UPDATE_USER_COMPANY, {userCompany: updatedUserCompany})
  },

  // @ts-ignore
  // tslint:disable-next-line:no-shadowed-variable
  [Actions.SET_ERROR]({commit, state}, [error, router]: [Error, Router]) {
    commit(Mutations.UPDATE_ERROR, {error})

    router.push({name: getHome(state.userCompany.type)})
  }
}


const store = new Vuex.Store({
  state,
  mutations,
  actions
})

store.subscribe((_mutation, lState) => {
  // Store the state object as a JSON string
  localStorage.setItem('store', JSON.stringify(lState))
})

export default store
